import { FC, memo, useState } from 'react';
import { Box, Button, Typography } from '@mui/material';
import { TruncatingTypography } from '@components';
import { titles } from '@utils/translations/en';
import { AppItem } from './AppAccessSidepanel';
import { AppAccessGamesListDialog } from './Sidepanel';
import { EditOutlined } from '@mui/icons-material';
import AddIcon from '@mui/icons-material/Add';

interface AppAccessProps {
  games: AppItem[];
  companyName: string;
  onEdit?: () => void;
}

const GAME_NAME_MAX_WIDTH = 120;

const GameCard: FC<{ game: AppItem }> = memo(({ game }) => (
  <Box sx={{ display: 'flex', alignItems: 'center', gap: '8px', minWidth: 0 }}>
    <Box
      component="img"
      src={game.imageUrl}
      loading="lazy"
      alt={game.name}
      sx={{
        width: 52,
        height: 52,
        borderRadius: '8px',
        objectFit: 'cover',
        backgroundColor: '#E8EAEB',
        flexShrink: 0,
      }}
    />
    <Box
      sx={{
        display: 'flex',
        flexDirection: 'column',
        gap: '4px',
        minWidth: 0,
        maxWidth: GAME_NAME_MAX_WIDTH,
      }}
    >
      <TruncatingTypography
        fontSize={12}
        fontWeight={500}
        sx={{ color: '#162936', lineHeight: 1.2 }}
      >
        {game.gameId}
      </TruncatingTypography>
      <TruncatingTypography
        fontSize={14}
        fontWeight={600}
        tooltipTitle={game.name}
        sx={{ color: '#162936', lineHeight: 1.2 }}
      >
        {game.name}
      </TruncatingTypography>
    </Box>
  </Box>
));

GameCard.displayName = 'GameCard';

const AppAccess: FC<AppAccessProps> = ({ games, companyName, onEdit }) => {
  const [isGamesDialogOpen, setIsGamesDialogOpen] = useState(false);

  return (
    <>
      <Box sx={{ display: 'flex', flexDirection: 'column', gap: '12px', py: '1rem' }}>
        <Box sx={{ display: 'grid', gridTemplateColumns: '1fr 1fr', alignItems: 'center' }}>
          <Box sx={{ display: 'flex', alignItems: 'center' }}>
            <Typography fontSize={16} fontWeight={700} sx={{ whiteSpace: 'nowrap' }}>
              {titles.APP_ACCESS}
            </Typography>
            {onEdit && (
              <Button
                variant="text"
                disableRipple
                sx={{
                  textTransform: 'none',
                  p: 0,
                  marginLeft: `${games.length > 0 ? 0 : 'auto'}`,
                  color: '#51B8B0',
                  '&:hover': { textDecoration: 'underline', backgroundColor: 'transparent' },
                }}
                onClick={onEdit}
              >
                {games.length > 0 ? (
                  <EditOutlined sx={{ fontSize: '1.5rem' }} />
                ) : (
                  <>
                    <AddIcon sx={{ height: '1rem', width: '1rem' }} />
                    <Typography fontSize={14} fontWeight={700} sx={{ color: '#51B8B0' }}>
                      Add Apps
                    </Typography>
                  </>
                )}
              </Button>
            )}
          </Box>
        </Box>
        {games.length > 0 && (
          <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: '1.5rem' }}>
            {games.slice(0, 4).map((game) => (
              <GameCard key={game.id} game={game} />
            ))}
            {games.length > 4 && (
              <Button
                variant="text"
                disableRipple
                sx={{
                  textTransform: 'none',
                  p: 0,
                  color: '#51B8B0',
                  '&:hover': { textDecoration: 'underline', backgroundColor: 'transparent' },
                }}
                onClick={() => setIsGamesDialogOpen(true)}
              >
                {`Show all (${games.length})`}
              </Button>
            )}
          </Box>
        )}
      </Box>
      <AppAccessGamesListDialog
        open={isGamesDialogOpen}
        onClose={() => setIsGamesDialogOpen(false)}
        companyName={companyName}
        games={games}
      />
    </>
  );
};

export default AppAccess;
